import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { API_URL } from '../config';

interface CallRecording {
  _id: string;
  callerNumber: string;
  calledNumber?: string;
  // agentId may be either an id string or a populated user object
  agentId?: string | { _id?: string; firstName?: string; lastName?: string };
  // callId refers to the related Call _id
  callId?: string | { _id?: string; callerNumber?: string; startTime?: string };
  // queueId may be either an id string or a populated queue object
  queueId?: string | { _id?: string; name?: string };
  // Also include convenience fields which may be provided by backend
  agentName?: string;
  queueName?: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  status: string;
  recording?: {
    hasRecording: boolean;
    autoRecording?: boolean;
    filename?: string;
    filePath?: string;
    duration?: number;
    size?: number;
    format?: string;
  };
}

interface CallRecordingContextType {
  recordings: CallRecording[];
  activeRecordings: CallRecording[];
  loading: boolean;
  error: string | null;
  fetchRecordings: (filters?: { agentId?: string; dateFrom?: string; dateTo?: string; callId?: string; limit?: number }) => Promise<CallRecording[] | undefined>;
  startRecording: (callId: string, agentId: string) => Promise<boolean>;
  stopRecording: (callId: string) => Promise<boolean>;
  downloadRecording: (recordingId: string) => Promise<void>;
  playRecording: (recordingId: string) => string;
  deleteRecording: (recordingId: string) => Promise<boolean>;
  getRecordingStatus: (callId: string) => CallRecording | null;
  isRecording: (callId: string) => boolean;
}

const CallRecordingContext = createContext<CallRecordingContextType | undefined>(undefined);

export const useCallRecording = () => {
  const context = useContext(CallRecordingContext);
  if (!context) {
    throw new Error('useCallRecording must be used within a CallRecordingProvider');
  }
  return context;
};

interface CallRecordingProviderProps {
  children: ReactNode;
}

export const CallRecordingProvider: React.FC<CallRecordingProviderProps> = ({ children }) => {
  const [recordings, setRecordings] = useState<CallRecording[]>([]);
  const [activeRecordings, setActiveRecordings] = useState<CallRecording[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch recordings (completed calls)
  const fetchRecordings = async (filters?: { agentId?: string; dateFrom?: string; dateTo?: string; callId?: string; limit?: number }) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.append('limit', '100'); // Get more records
      if (filters?.agentId) params.append('agentId', filters.agentId);
      if (filters?.callId) params.append('callId', filters.callId);
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.append('dateTo', filters.dateTo);
      
      const response = await api.get(`/recordings?${params.toString()}`);
      const list = response.data.data || response.data || [];
      setRecordings(list);
      return list;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kayıtlar yüklenirken hata oluştu');
      console.error('Recordings fetch error:', err);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  // Start recording
  const startRecording = async (callId: string, agentId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Create recording record in database
      const response = await api.post('/recordings', {
        callId,
        agentId,
        status: 'recording',
        startTime: new Date().toISOString()
      });

      const newRecording = response.data;
      setActiveRecordings(prev => [...prev, newRecording]);
      
      console.log('✅ Recording started for call:', callId);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kayıt başlatılırken hata oluştu');
      console.error('Start recording error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Stop recording
  const stopRecording = async (callId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Find active recording
      const activeRecording = activeRecordings.find(rec => (typeof rec.callId === 'string' ? rec.callId === callId : (rec.callId as any)?._id === callId));
      if (activeRecording) {
        // Update recording status
        await api.put(`/recordings/${activeRecording._id}`, {
          status: 'completed',
          endTime: new Date().toISOString()
        });

        // Move from active to completed recordings
        setActiveRecordings(prev => prev.filter(rec => (typeof rec.callId === 'string' ? rec.callId !== callId : (rec.callId as any)?._id !== callId)));
        setRecordings(prev => [...prev, {
          ...activeRecording,
          status: 'completed',
          endTime: new Date().toISOString()
        }]);
      }
      
      console.log('✅ Recording stopped for call:', callId);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kayıt durdurulurken hata oluştu');
      console.error('Stop recording error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Download recording
  const downloadRecording = async (callId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const baseURL = API_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = `${baseURL}/calls/${callId}/recording/download?token=${token}`;
      link.download = `recording_${callId}.wav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ Recording download started:', callId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kayıt indirilirken hata oluştu');
      console.error('Download recording error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ✨ Play recording in browser - returns audio URL for modal player
  const playRecording = (callId: string): string => {
    try {
      const baseURL = API_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      const audioUrl = `${baseURL}/calls/${callId}/recording/stream?token=${token}`;

      console.log('✅ Recording audio URL generated:', callId);
      return audioUrl;
    } catch (err: any) {
      console.error('Play recording error:', err);
      setError('Kayıt oynatılırken hata oluştu');
      return '';
    }
  };

  // Delete recording
  const deleteRecording = async (recordingId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      await api.delete(`/recordings/${recordingId}`);
      
      // Remove from local state
      setRecordings(prev => prev.filter(rec => rec._id !== recordingId));
      setActiveRecordings(prev => prev.filter(rec => rec._id !== recordingId));
      
      console.log('✅ Recording deleted:', recordingId);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kayıt silinirken hata oluştu');
      console.error('Delete recording error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get recording status for a call
  const getRecordingStatus = (callId: string): CallRecording | null => {
    const activeRecording = activeRecordings.find(rec => (typeof rec.callId === 'string' ? rec.callId === callId : (rec.callId as any)?._id === callId));
    if (activeRecording) return activeRecording;
    
    const completedRecording = recordings.find(rec => (typeof rec.callId === 'string' ? rec.callId === callId : (rec.callId as any)?._id === callId));
    return completedRecording || null;
  };

  // Check if a call is being recorded
  const isRecording = (callId: string): boolean => {
    return activeRecordings.some(rec => (typeof rec.callId === 'string' ? rec.callId === callId : (rec.callId as any)?._id === callId));
  };

  const value: CallRecordingContextType = {
    recordings,
    activeRecordings,
    loading,
    error,
    fetchRecordings,
    startRecording,
    stopRecording,
    downloadRecording,
    playRecording,
    deleteRecording,
    getRecordingStatus,
    isRecording,
  };

  return (
    <CallRecordingContext.Provider value={value}>
      {children}
    </CallRecordingContext.Provider>
  );
};

export default CallRecordingContext;

